(ns nosco.ui.select2
  (:require
   ["react" :as React]
   ["react-keyed-flatten-children" :default flatten-children]
   [cljs-bean.core :as bean]
   [goog.object :as gobj]
   [hx.hooks :as hooks]
   [hx.react :refer [defnc] :as hx]
   [medley.core :as medley]
   [nosco.colors :as colors]
   [nosco.hooks :as nosco-hooks]
   [nosco.icons2 :as icons]
   [nosco.interop.downshift :refer [use-select]]
   [nosco.react :as nosco-react]
   [nosco.ui.avatar :as avatar]
   [nosco.ui.inputs :as inputs]
   [nosco.ui.popup :as popup]
   [nosco.ui.text :as text]
   [nosco.util :as util]
   [reseda.react :as rr]))

(defn- valid-variant? [variant]
  (contains? #{:default :condensed} variant))

(defnc Option [{:keys [label sublabel value icon img-icon-src has-img icon-right disabled]}])
(defnc Divider [])
(gobj/set Divider "select2-type" ::DIVIDER)
(defnc SectionHeader [{:keys [label]}])
(gobj/set SectionHeader "select2-type" ::HEADER)
(defn- children->options [children]
  (->> (seq (flatten-children children))
       (map
        (fn [el]
          (let [props (.-props el)
                type (.-type el)
                select-type (gobj/get type "select2-type")
                label (.-label props)
                value (.-value props)]
            (case select-type
              ::HEADER (assert (some? label) "Header should have a label")
              ::DIVIDER nil
              nil (do
                    (assert (some? label) "Option should have a label")
                    (assert (some? value) "Option should have a value")))
            (assoc
             (bean/->clj props)
             ::type select-type))))))

(defn index-of
  "Return the first index of x inside a seq of xs, or -1"
  [xs x]
  (let [idx
        (->> xs
             (medley/indexed)
             (medley/find-first (fn [[_idx item]]
                                  (= item x)))
             (first))]
    (or idx -1)))

(defn- state-reducer [items state actionAndChanges]
  ;; https://github.com/downshift-js/downshift/issues/645#issuecomment-451805753
  ;; highlight the selected item, so that the menu scrolls to it on open
  (let [{:keys [type ^js changes]} (bean/bean actionAndChanges)
        prev-open (.-isOpen state)
        now-open (.-isOpen changes)
        selected-item (.-selectedItem changes)
        value (when selected-item (.-value selected-item)) ;; js accessor of .-value since this is a Downshift callback
        highlighted-index (when value (index-of (mapv :value items) value))]
    (if (and (not prev-open) now-open)
      (let [new-changes (-> changes
                            bean/bean
                            (assoc :highlightedIndex highlighted-index)
                            bean/->js)]
        new-changes)
      changes)))

;; we have items that render but should not be highlighted
;; to do this, we break the highlighted index out of a simple map-indexed
;; to an index that only increments for selectable options
(defn use-select-options [options children]
  (let [options (or options (children->options children))
        items (atom [])
        index (atom 0)
        options-with-index (vec (for [item options]
                                  (if (or (::type item) (:disabled item))
                                   item
                                   (let [idx @index]
                                     (swap! index inc)
                                     (swap! items conj item)
                                     (assoc item ::index idx)))))]
    {:items @items
     :options options-with-index}))

(defnc FakeSelectButton [{:keys [variant label placeholder error children local-selected-item on-remove] :as props} ref]
  [:button
   (-> {:ref ref
        :data-form-field-interactive true
        :class ["nosco-select2"
                (when error "nosco-select2--error")
                (when (= variant :condensed) "nosco-select2--condensed")]
        :type "button"}
       (nosco-react/merge-props (dissoc props :onBlur :variant :label :placeholder :error :local-selected-item :on-remove)))
   [:span
    {:title label
     :class ["nosco-select2__text"
             "nosco-select2--selected"
             (when placeholder "nosco-select2__placeholder")]}
    (or children label)]
   (when (and on-remove
              local-selected-item
              (not (:placeholder local-selected-item)))
     [icons/Icon {:icon icons/Remove-small
                  :color (if (:disabled props)
                           colors/neutral-300
                           colors/neutral-900)
                  :on-click (fn [e]
                              (.preventDefault e)
                              (.stopPropagation e)
                              (on-remove e))}])
   [icons/Icon {:icon icons/DropdownSm-small
                :color (if (:disabled props)
                         colors/neutral-300
                         colors/neutral-900)}]])

(nosco-react/forward-ref! FakeSelectButton)

(defnc ^:private UserSelectOption [{:keys [label index item selected item-props]}]
  (let [{:keys [sublabel user]} item]
    [:li (merge {:key (str label ":" index)}
                item-props
                {:class ["nosco-select-option2"
                         "nosco-select-option2--user"
                         (when selected
                           "nosco-select-option2__selected")]})
     [:div {:class "nosco-select-option2__avatar"}
      [avatar/Avatar {:user user}]]
     [:div {:class "nosco-select-option2__label"
            :title label}
      [text/Text {:variant text/Body-Sm-Bold}
       label]
      [:br]
      [text/Text {:variant text/Body-Sm-Regular}
       sublabel]]]))

(defnc ^:private SelectOption [{:keys [label index item selected item-props]}]
  [:li (merge {:key (str label ":" index)}
              item-props
              {:class ["nosco-select-option2"
                       (when selected
                         "nosco-select-option2__selected")
                       (when (:indent? item)
                         "nosco-select-option2--indent")
                       (when (and (not selected) (:disabled item))
                         "nosco-select-option2--disabled")]})
   (when-let [icon (:icon item)]
     [:span {:class "nosco-select2__icon"}
      [icons/Icon
       {:icon icon
        :aria-hidden true}]])
   (when (:has-img item)
     [:span {:class ["nosco-select2__icon" "nosco-select2__icon--img"]}
      (when-let [img-icon-src (:img-icon-src item)]
        [:img
         {:src img-icon-src
          :aria-hidden true}])])
   [:span {:class "nosco-select-option2__label"
           :title label}
    label]
   (when-let [icon-props (:icon-right item)]
     [:span {:class "nosco-select2__icon-right"}
      [icons/Icon
       (assoc icon-props
              :aria-hidden true)]])])

(defnc CustomSelect
  [{:keys [variant
           options
           children
           value
           on-change
           default-value
           placeholder
           disabled
           loading
           on-search
           search-placeholder
           name
           error
           label
           on-is-open-change
           option-type
           selected-option-comp
           on-remove] :or {variant :default
                           placeholder "Select an option..."
                           option-type :generic}}]
  (when-not (valid-variant? variant)
    (throw (ex-info (str "Invalid select variant: " variant) {})))
  (let [item-to-string #(:label %)
        item-value #(:value %)
        [search-str set-search-str] (hooks/useState "")
        option-comp (case option-type
                      :generic SelectOption
                      :user UserSelectOption)
        {:keys [items options]} (use-select-options options children)
        find-item #(first (filter (fn [item] (= (item-value item) %))
                                  items))
        ;; `local-selected-item` is used to always display the selected option, even when `(find-item value)`
        ;; returns nil because the user is filtering the options by typing in the input field
        [local-selected-item set-local-selected-item] (hooks/useState nil)
        {:keys
         [get-toggle-button-props
          get-menu-props
          get-item-props
          get-label-props
          open?
          selected-item]}
        (use-select {:items items
                     :item-to-string item-to-string
                     :state-reducer (partial state-reducer items)
                     :on-is-open-change on-is-open-change
                     ;; We have to use empty object when :selected-item
                     ;; is not yet set; otherwise Downshift assumes
                     ;; uncontrolled state; switching between uncontrolled
                     ;; and controlled states between renders is not recommended
                     ;; and Downshift throws if you do that
                     ;; however if no on-change is passed, then we assume uncontrolled component
                     :selected-item (when on-change (or local-selected-item {}))
                     :initial-selected-item (when default-value (find-item default-value))
                     :on-selected-item-change #(when on-change (-> %
                                                                   :selected-item
                                                                   item-value
                                                                   on-change))})
        [button-ref-cb popper] (popup/use-popper {:placement "bottom-start"
                                                  :flip true
                                                  :use-ref-width? true})
        input-ref (React/useRef)
        list-id (:id (get-menu-props))
        items-value* (hooks/useValue items)]
    (hooks/useEffect
     (fn []
       (when value
         ;; Guards against temporary invalid state where value is not nil but
         ;; (find-item value) is nil because the value and options props are
         ;; inconsitent (ie. not updated at the same time)
         (when-let [found-item (find-item value)]
           (set-local-selected-item found-item))))
     [value items-value*])
    (hooks/useLayoutEffect
     (fn []
       (when open?
         (when (popup/popper-initialized? popper)
           (let [rects (.-rects (.-state (:popper popper)))
                 ref-rect (.-reference rects)]
             ;; in some cases, popper thinks the "reference" element
             ;; is non-existent (that is, the select button in this case)
             ;; this is reflected as having a rect of zero height
             ;; since the select button should never have a zero height,
             ;; we ask popper to update until it detects the right height
             (when (zero? (.-height ref-rect))
               (.update (:popper popper))))))
       js/undefined))
    (hooks/useLayoutEffect
     (fn []
       ;; the select trigger might have moved since popper last update
       ;; so do an update when the select opens to make sure
       ;; we are at the correct place
       (when open?
         (.update (:popper popper))
         (js/setTimeout
          #(when-let [input (.-current input-ref)]
             (.focus input))
          0))
       (when-not open?
         (set-search-str ""))
       js/undefined)
     [open?])
    (hooks/useLayoutEffect
     ;; when the items change, it must be through a search
     ;; so scroll the list to the top
     (fn []
         (when-let [list (.getElementById js/document list-id)]
           (set! (.-scrollTop list) 0))
       js/undefined)
     [items-value*])
    [:<>
     (when label
       [:label (merge (get-label-props) {:class "nosco-label"})
        label])
     ;; pass in name to be able to use this inside an HTML form element
     (when name
       [:input {:type "hidden" :name name
                ;; clj accessor of :value since this is converted via our interop layer
                :value (str (:value selected-item))}])
     (let [toggle-button-props (get-toggle-button-props)
           ;; nosco.react/merge-refs returns a memoized callback but (:ref toggle-button-props)
           ;; is a new ref on every render. Avoid breaking the nosco.react/merge-refs memoization
           ;; by making (:ref toggle-button-props) memoized
           toggle-button-ref-callback (hooks/useCallback (:ref toggle-button-props) [])
           selected-label (item-to-string selected-item)
           placeholder? (empty? selected-label)
           label (or selected-label placeholder)]
       [FakeSelectButton
        (merge toggle-button-props
               {:variant variant
                :error error
                :disabled disabled
                :ref (nosco-react/merge-refs toggle-button-ref-callback button-ref-cb)
                :label label
                :placeholder placeholder?
                :local-selected-item local-selected-item
                :on-remove on-remove})
        (if selected-option-comp
          [selected-option-comp {:item (if (:label selected-item)
                                         selected-item
                                         {:label placeholder})}]
          label)])
     ;; follow the Downshift guidance, and always render the menu container
     ;; in this case the overlay/ul, but only render the li items when
     ;; the menu is open -- fixes an annoying page jump
     [popup/Overlay
      {:open true
       :popper popper
       :lock-focus true}
      [:div (merge (get-menu-props) {:class ["nosco-select-list" "nosco-dropdown"
                                             (when on-search "nosco-select-list--with-search")
                                             (when-not open? "nosco-select-list--closed")]
                                     :onBlur #(.preventDefault %)})
       (when (and open? on-search)
         [:div {:class "nosco-select2-search"}
          [inputs/SearchInput
           {:ref input-ref
            :placeholder search-placeholder
            :loading loading
            :value search-str
            ;; pressing space will close the select, so if we're typing
            ;; just don't propagate the event
            :on-key-down (fn [e]
                            (when (= " " (.-key e))
                              (.stopPropagation e)))
            :on-change (fn [e]
                         (let [s (-> e .-target .-value)]
                           (set-search-str s)
                           (on-search s)))}]])
       (when open?
         [:ul {:style {:opacity (if loading 0.5 1)}}
          (map
           (fn [item]
             (let [label (item-to-string item)
                   index (::index item)
                   type (::type item)]
               (case type
                 ::DIVIDER
                 [:li {:aria-hidden true :class "nosco-select2__divider"}]

                 ::HEADER
                 [:li {:class "nosco-select2__header"} (:label item)]

                   ;; else
                 [option-comp {:label label
                               :index index
                               :item item
                               :item-props (when-not (:disabled item)
                                             (get-item-props {:item label :index index}))
                               :selected (= (:value item) (:value selected-item))}])))
           options)])]]]))

(defn- grouped-options
  "Returns a tree of options and optgroups."
  [options]
  (loop [options options
         result []]
    (cond
      (empty? options)
      result

      (= ::HEADER (::type (first options)))
      (let [[group-options options'] (split-with #(nil? (::type %)) (rest options))]
        (recur options'
               (conj result {:group true
                             :label (:label (first options))
                             :options group-options})))

      (and (= ::DIVIDER (::type (first options)))
           (= ::HEADER (::type (second options))))
      (let [[group-options options'] (split-with #(nil? (::type %)) (rest (rest options)))]
        (recur options'
               (conj result {:group true
                             :label (:label (second options))
                             :options group-options})))

      (= ::DIVIDER (::type (first options)))
      (let [[group-options options'] (split-with #(nil? (::type %)) (rest options))]
        (recur options'
               (conj result {:group true
                             :options group-options})))

      :else
      (recur (rest options) (conj result (first options))))))

;; The issues with custom select and using suspending...
;; 1. You want to trigger the fetch only when the select opens
;; 2. You want the suspense boundary to be in the popup, not outside
;; 3. If there's a selected value, you want to show that in the button
;; 4. You also want to use a cached suspending when fetching
;; 5. But, downshift needs an items array to begin with, so you have to start with that empty
;;    - the array is needed to manage interaction and highlighted index
;;
;; This probably points to having either:
;; - a downshift-select built around suspending values
;; - adding loading states and dealing with the data fetching outside,
;;   with no support for suspense boundaries
;;
;; The following solution is a compromise:
;; 1. Until the first fetch, we show a disabled select - no fetching when the select opens
;; 2. Can't show a selected value until the fetch completes - assuming the selected value is in the initial results

(defnc SuspendingSelectInner [{:keys [options*] :as props}]
  (let [[options* loading] (rr/useCachedSuspending options*)
        options (rr/deref* options*)]
    [CustomSelect (-> props
                      (dissoc options*)
                      (assoc :loading loading
                             :options options))]))

(defnc SuspendingSelect [{:keys [options*] :as props}]
  [React/Suspense {:fallback (hx/f [CustomSelect (-> props
                                                     (assoc :options []
                                                            :disabled true)
                                                     (dissoc :options*))])}
   [SuspendingSelectInner props]])

(defnc NativeSelect
  [{:keys [label
           options
           children
           placeholder
           name
           disabled
           error
           default-value
           value
           on-change]
    :or {placeholder "Select an option..."
         value ::missing}}]
  (let [{:keys [options]} (use-select-options options children)
        select-id (nosco-hooks/use-id "NativeSelect")
        select-ref (React/useRef)
        value-actual (when-not (= ::missing value) value)]
    (hooks/useEffect
     (fn []
       (when (or value-actual default-value)
         (.. select-ref -current -classList (add "nosco-native-select--has-value")))
       ;; Attach event listener manually to avoid marking the select as controlled
       (.. select-ref -current
           (addEventListener
            "change"
            (fn []
              (let [select (.-current select-ref)]
                (if (= "" (.-value select))
                  (.. select -classList (remove "nosco-native-select--has-value"))
                  (.. select -classList (add "nosco-native-select--has-value"))))))))
     [])
    [:<>
     (when label
       [:label {:class ["nosco-label"] :for select-id}
        label])
     [:div {:class ["nosco-native-select-outer"]}
      [:select
       {:ref select-ref
        :id select-id
        :class ["nosco-native-select"
                (when error "nosco-native-select--error")]
        :name name
        :disabled disabled
        :data-form-field-interactive true
        ;; if value is present in the props, but nil, we want to have a controlled component.
        ;; so don't set default-value otherwise React will complain loudly
        :default-value (if (= ::missing value) (or default-value "") js/undefined)
        :value (or value-actual js/undefined)
        :on-change
        (when on-change
          (fn [event]
            (on-change (:value (first (filter #(= (.. event -target -value) (str (:value %))) options))))))}
       (when placeholder
         [:option {:value "" :hidden true} placeholder])
       (for [option (grouped-options options)]
         (if (:group option)
           [:optgroup {:label (:label option)}
            (for [{:keys [value label]} (:options option)]
              [:option {:value value} label])]
           [:option {:value (:value option)} (:label option)]))]
      [:div {:class ["nosco-native-select-chevron"]}]]]))

(defnc Select [props]
  (let [mobile (util/use-is-mobile?)]
    (if mobile
      [NativeSelect props]
      [CustomSelect props])))
